import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from '../../../_core/_services/auth.service';
import { ApiService } from '../../../_core/_services/api.service';
import { AdminMenuCountersDto } from '../../../_core/_model/admin-menu-counters';
import { UserRole } from '../../../_core/_model/user-role';

interface AdminDropdownBadgeCounts {
  tickets: string;
  countries: string;
  currencies: string;
  kycReviews: string;
  paymentProviders: string;
  gatewayConfigs: string;
  withdrawalGateways: string;
  withdrawalRequests: string;
  orders: string;
  users: string;
}

@Component({
  selector: 'app-admin-dropdown',
  standalone: false,
  templateUrl: './admin-dropdown.html',
  styleUrl: './admin-dropdown.css',
})
export class AdminDropdown implements OnInit, OnDestroy {
  isStaff$!: Observable<boolean>;
  currentRole: UserRole | null = null;

  @Input() page: string = 'Dashboard';
  @Input() selectedUrl: string = '';

  private sub = new Subscription();

  badgeCounts: AdminDropdownBadgeCounts = {
    tickets: '0',
    countries: '0',
    currencies: '0',
    kycReviews: '0',
    paymentProviders: '0',
    gatewayConfigs: '0',
    withdrawalGateways: '0',
    withdrawalRequests: '0',
    orders: '0',
    users: '0'
  };

  constructor(
    private auth: AuthService,
    private api: ApiService
  ) {}

  ngOnInit(): void {
    this.isStaff$ = this.auth.isStaff$();

    this.sub.add(this.auth.currentUser$.subscribe(user => {
      this.currentRole = (user?.role as UserRole | undefined) ?? null;
    }));

    this.sub.add(this.isStaff$.subscribe(isStaff => {
      if (isStaff) this.loadAdminMenuCounters();
    }));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get isAdmin(): boolean {
    return this.currentRole === 'Admin' || this.currentRole === 'SuperAdmin';
  }

  get canCompliance(): boolean {
    return this.isAdmin || this.currentRole === 'Compliance';
  }

  get canFinance(): boolean {
    return this.isAdmin || this.currentRole === 'Finance';
  }

  get canSupport(): boolean {
    return this.isAdmin || this.currentRole === 'Support';
  }

  get canHouseManagement(): boolean {
    return this.currentRole === 'Finance' || this.currentRole === 'SuperAdmin';
  }

  get canHouseMarketMaker(): boolean {
    return this.currentRole === 'SuperAdmin';
  }

  get menuLabel(): string {
    switch (this.currentRole) {
      case 'Compliance': return 'Compliance';
      case 'Finance': return 'Finance';
      case 'Support': return 'Support';
      default: return 'Administrator';
    }
  }

  loadAdminMenuCounters(): void {
    this.api.getAdminMenuCounters().subscribe({
      next: (res) => {
        const data: AdminMenuCountersDto | null = res.data || null;
        if (!data) return;

        this.badgeCounts = {
          tickets: data.tickets?.display || '0',
          countries: data.countries?.display || '0',
          currencies: data.currencies?.display || '0',
          kycReviews: data.kycReviews?.display || '0',
          paymentProviders: data.paymentProviders?.display || '0',
          gatewayConfigs: data.gatewayConfigs?.display || '0',
          withdrawalGateways: data.withdrawalGateways?.display || '0',
          withdrawalRequests: data.withdrawalRequests?.display || '0',
          orders: data.orders?.display || '0',
          users: data.users?.display || '0'
        };
      },
      error: () => {
        // Role-scoped counters are optional; retain zero badges when unavailable.
      }
    });
  }

  badgeClass(type: 'red' | 'green'): string {
    return type === 'red' ? 'text-bg-danger' : 'text-bg-success';
  }
}
